import { createFileRoute } from '@tanstack/react-router'
import { useSession } from "@/lib/auth/auth";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import { useState } from "react";
import { toast } from "sonner";

import { companySetupApi } from "@/apis/custom/companySetup";
import { MainLayout } from "@/components/layouts/main-layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useGetAllCompanies } from "@/queries/companies/get-all";

export const Route = createFileRoute('/_authenticated/admin/company-setup')({
  component: AdminCompanySetup,
})

function AdminCompanySetup() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  const { companies, loading: companiesLoading } = useGetAllCompanies();

  const sortedCompanies = React.useMemo(() => {
    return [...companies].sort((a, b) =>
      a.companyName.localeCompare(b.companyName),
    );
  }, [companies]);

  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [vatId, setVatId] = useState("");
  const [userEmails, setUserEmails] = useState("");
  const [accountLength, setAccountLength] = useState(4);
  const [vorsteuerabzug, setVorsteuerabzug] = useState(false);
  const [ocrReview, setOcrReview] = useState(false);
  const [suppliersCsv, setSuppliersCsv] = useState<File | null>(null);
  const [accountsCsv, setAccountsCsv] = useState<File | null>(null);
  const [costCentersCsv, setCostCentersCsv] = useState<File | null>(null);

  const [userAccessEmails, setUserAccessEmails] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [useEmailFilter, setUseEmailFilter] = useState(false);

  if (isPending || companiesLoading) {
    return <div>Loading...</div>;
  }

  if (!session || session?.user?.systemRole !== "superadmin") {
    navigate({ to: "/" });
    return null;
  }

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompany(companyId);
  };

  const handleGiveAccess = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    try {
      const payload = useEmailFilter
        ? { emailFilter: emailFilter, isFilter: true }
        : {
            emails: userAccessEmails.split(",").map((email) => email.trim()),
            isFilter: false,
          };

      await companySetupApi.giveAccess({
        ...payload,
      });
      toast.success("Access granted successfully");
    } catch (error) {
      toast.error("Failed to grant access");
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await companySetupApi.createTenant({
        companyName,
        companyDescription,
        vatId,
        userEmails: userEmails.split(",").map((email) => email.trim()),
        accountLength,
        vorsteuerabzug,
        ocrReview,
      });
      toast.success("Tenant created successfully");
    } catch (error) {
      toast.error("Failed to create tenant");
    }
  };

  const handleUploadSuppliers = async () => {
    if (!suppliersCsv || !selectedCompany) return;
    const formData = new FormData();
    formData.append("file", suppliersCsv);
    try {
      await companySetupApi.uploadSuppliers(formData);
      toast.success("Suppliers uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload suppliers");
    }
  };

  const handleUploadAccounts = async () => {
    if (!accountsCsv || !selectedCompany) return;
    const formData = new FormData();
    formData.append("file", accountsCsv);
    try {
      await companySetupApi.uploadAccounts(formData);
      toast.success("Accounts uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload accounts");
    }
  };

  const handleUploadCostCenters = async () => {
    if (!costCentersCsv || !selectedCompany) return;
    const formData = new FormData();
    formData.append("file", costCentersCsv);
    try {
      await companySetupApi.uploadCostCenters(formData);
      toast.success("Cost centers uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload accounts");
    }
  };

  const getSelectedCompanyName = () => {
    return (
      companies.find((company: any) => company.companyId === selectedCompany)
        ?.companyName || ""
    );
  };

  return (
    <MainLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Admin Company Setup</h1>

        <div className="mb-4">
          <Select
            onValueChange={handleCompanySelect}
            value={selectedCompany || undefined}
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select a company" />
            </SelectTrigger>
            <SelectContent>
              {sortedCompanies.map((company: any) => (
                <SelectItem key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-8">
          <form onSubmit={handleCreateTenant} className="space-y-4">
            <h2 className="text-xl font-semibold mb-2">Create New Tenant</h2>
            <p className="text-sm text-gray-600 mb-4">
              Instructions:
              <br />
              1. Fill in company details and create tenant
              <br />
              2. Reload page
              <br />
              3. Select new tenant in dropdown
              <br />
              4. Upload suppliers and accounts
              <br />
              5. When ready, give access to further users.
            </p>
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="companyDescription">Company Description</Label>
              <Textarea
                id="companyDescription"
                value={companyDescription}
                onChange={(e) => setCompanyDescription(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vatId">VAT ID</Label>
              <Input
                id="vatId"
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="userEmails">User Emails (comma-separated)</Label>
              <Input
                id="userEmails"
                value={userEmails}
                onChange={(e) => setUserEmails(e.target.value)}
                placeholder="user1@example.com, user2@example.com"
              />
            </div>
            <div>
              <Label htmlFor="accountLength">Account Length</Label>
              <Input
                id="accountLength"
                type="number"
                value={accountLength}
                onChange={(e) => setAccountLength(Number(e.target.value))}
                min={1}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vorsteuerabzug"
                checked={vorsteuerabzug}
                onCheckedChange={(checked) =>
                  setVorsteuerabzug(checked as boolean)
                }
              />
              <Label htmlFor="vorsteuerabzug">Vorsteuerabzug</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="ocrReview"
                checked={ocrReview}
                onCheckedChange={(checked) => setOcrReview(checked as boolean)}
              />
              <Label htmlFor="ocrReview">OCR Review</Label>
            </div>
            <Button type="submit">Create Tenant</Button>
          </form>

          <div>
            <h2 className="text-xl font-semibold mb-2">
              Upload Suppliers CSV{" "}
              {selectedCompany ? `for ${getSelectedCompanyName()}` : ""}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Must conform to standard DATEV Suppliers CSV Format: Column
              headers must be on second row, must contain columns Name
              (Adressattyp Unternehmen), EU-UStID, Konto. Can contain other
              columns, these will be discarded.
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setSuppliersCsv(e.target.files?.[0] || null)}
            />
            <Button
              onClick={handleUploadSuppliers}
              className="mt-2"
              disabled={!selectedCompany}
            >
              Upload Suppliers
            </Button>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">
              Upload Accounts CSV{" "}
              {selectedCompany ? `for ${getSelectedCompanyName()}` : ""}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Must conform to standard DATEV Accounts CSV Format: Column headers
              must be on second row, must contain columns Konto von,
              Beschriftung, Definition. All other columns will be discarded.
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setAccountsCsv(e.target.files?.[0] || null)}
            />
            <Button
              onClick={handleUploadAccounts}
              className="mt-2"
              disabled={!selectedCompany}
            >
              Upload Accounts
            </Button>
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">
              Upload Cost Centers CSV{" "}
              {selectedCompany ? `for ${getSelectedCompanyName()}` : ""}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Must conform to standard DATEV Cost Centers CSV Format: Column
              headers must be first row, must contain columns:
              Kostenstelle/-träger, Kurzbezeichnung, Langbezeichnung
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setCostCentersCsv(e.target.files?.[0] || null)}
            />
            <Button
              onClick={handleUploadCostCenters}
              className="mt-2"
              disabled={!selectedCompany}
            >
              Upload Cost Centers
            </Button>
          </div>
        </div>
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-2">
            Give Users Access{" "}
            {selectedCompany ? `to ${getSelectedCompanyName()}` : ""}
          </h2>
          {selectedCompany && (
            <a
              href={`https://tabular.metabaseapp.com/question/119-user-accesses-by-id?CompanyId=${encodeURIComponent(
                selectedCompany,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline mb-4 inline-block"
            >
              View Current User Accesses in Metabase
            </a>
          )}
          <p className="text-sm text-gray-600 mb-4">
            You can either pass a comma-separated list of user emails, or you
            can set an email filter, which means that we will give access to all
            users whose email contains this substring.
          </p>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useEmailFilter"
                checked={useEmailFilter}
                onCheckedChange={(checked) =>
                  setUseEmailFilter(checked as boolean)
                }
              />
              <Label htmlFor="useEmailFilter">Use Email Filter</Label>
            </div>
            {useEmailFilter ? (
              <div>
                <Label htmlFor="emailFilter">Email Filter</Label>
                <Input
                  id="emailFilter"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="e.g. @company.com"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="userAccessEmails">
                  User Emails (comma-separated)
                </Label>
                <Input
                  id="userAccessEmails"
                  value={userAccessEmails}
                  onChange={(e) => setUserAccessEmails(e.target.value)}
                  placeholder="user1@example.com, user2@example.com"
                />
              </div>
            )}
            <Button onClick={handleGiveAccess} disabled={!selectedCompany}>
              Give Access
            </Button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminCompanySetup;
